/**
 * RELEASE INFO CHIPS
 *
 * Compact chip row used on item Cards (list/grid views) to summarize
 * release info in a small amount of vertical space. Matches the
 * pattern PolishCard already uses so decals/plates/tools/etc. show
 * the same signals.
 *
 * Renders (whichever are populated, in this order):
 *   - Release type badge (📦 Box, 🏠 Core, ⭐ Exclusive, etc.)
 *   - Collection chip (linkified with onFilterByCollection)
 *   - Event chip (linkified with onFilterByEvent)
 *   - Season/year pill (🌷 Spring 2018 style)
 *
 * NOT shown here (kept for the DetailModal): box/group/exclusive/
 * charity/convention names, themes. The card is a compact summary;
 * for full breakdown users click through to the modal.
 */

import { Sparkles, Package } from 'lucide-react';

export interface ReleaseInfoChipsItem {
  releaseType?: string;
  collectionName?: string;
  /** Legacy alias — some old items still use `collection`. */
  collection?: string;
  eventName?: string;
  /** Legacy alias — pre-Jan-2026 field. */
  eventSource?: string;
  /** Older legacy alias. */
  event?: string;
  releaseYear?: number | string;
  releaseMonth?: number | string;
  releaseSeason?: string;
}

interface Props {
  item: ReleaseInfoChipsItem;
  /** Optional click handlers — when provided the corresponding chip
   *  becomes clickable to filter the list. */
  onFilterByCollection?: (collection: string) => void;
  onFilterByEvent?: (event: string) => void;
}

/** Shared date-display logic — prefer month (specific) over season, fall
 *  through to season for legacy items that don't have month. */
function getReleaseDateDisplay(item: ReleaseInfoChipsItem) {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthToSeason: Record<number, string> = {
    1: 'Winter', 2: 'Winter', 3: 'Spring', 4: 'Spring', 5: 'Spring',
    6: 'Summer', 7: 'Summer', 8: 'Summer', 9: 'Fall', 10: 'Fall', 11: 'Fall', 12: 'Winter',
  };

  const monthNum = typeof item.releaseMonth === 'string' ? parseInt(item.releaseMonth) : item.releaseMonth;
  const effectiveSeason = monthNum ? monthToSeason[monthNum] : item.releaseSeason;

  let text = '';
  if (monthNum) text = monthNames[monthNum - 1];
  else if (item.releaseSeason) text = item.releaseSeason;
  if (item.releaseYear) text = text ? `${text} ${item.releaseYear}` : `${item.releaseYear}`;
  return { text, effectiveSeason };
}

/** Release-type → badge tuple. `null` means "don't render a badge" —
 *  used for `unknown`, `event`, `collection` (those show their own
 *  specific chips inline) and unset. */
function getReleaseTypeDisplay(releaseType: string | undefined) {
  if (!releaseType || ['unknown', 'event', 'collection'].includes(releaseType)) return null;
  const typeMap: Record<string, { emoji: string; label: string; color: string }> = {
    core: { emoji: '🏠', label: 'Core', color: 'bg-gray-100 text-gray-700' },
    box: { emoji: '📦', label: 'Box', color: 'bg-amber-50 text-amber-700' },
    groupCustom: { emoji: '👥', label: 'Group', color: 'bg-cyan-50 text-cyan-700' },
    exclusive: { emoji: '⭐', label: 'Exclusive', color: 'bg-yellow-50 text-yellow-700' },
    charity: { emoji: '💝', label: 'Charity', color: 'bg-pink-50 text-pink-700' },
    limitedEdition: { emoji: '✨', label: 'LE', color: 'bg-rose-50 text-rose-700' },
    convention: { emoji: '🎫', label: 'Convention', color: 'bg-teal-50 text-teal-700' },
    collab: { emoji: '🤝', label: 'Collab', color: 'bg-violet-50 text-violet-700' },
    other: { emoji: '📝', label: 'Other', color: 'bg-gray-50 text-gray-600' },
  };
  return typeMap[releaseType] || null;
}

export default function ReleaseInfoChips({
  item,
  onFilterByCollection,
  onFilterByEvent,
}: Props) {
  const collectionValue = item.collectionName || item.collection || '';
  const eventValue = item.eventName || item.eventSource || item.event || '';
  const hasCollection = collectionValue.trim() !== '';
  const hasEvent = eventValue.trim() !== '';
  const hasReleaseDate = !!(item.releaseSeason || item.releaseYear || item.releaseMonth);
  const releaseTypeDisplay = getReleaseTypeDisplay(item.releaseType);

  if (!releaseTypeDisplay && !hasCollection && !hasEvent && !hasReleaseDate) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-2">
      {releaseTypeDisplay && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${releaseTypeDisplay.color}`}
          title={`Release Type: ${releaseTypeDisplay.label}`}
        >
          <span>{releaseTypeDisplay.emoji}</span>
          <span>{releaseTypeDisplay.label}</span>
        </span>
      )}
      {hasCollection && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onFilterByCollection) onFilterByCollection(collectionValue);
          }}
          className={`inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium hover:bg-indigo-100 transition-default truncate max-w-full font-body ${
            onFilterByCollection ? 'cursor-pointer' : 'cursor-default'
          }`}
          title={`Collection: ${collectionValue}`}
        >
          <Sparkles className="icon-xs flex-shrink-0" />
          <span className="truncate">{collectionValue}</span>
        </button>
      )}
      {hasEvent && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onFilterByEvent) onFilterByEvent(eventValue);
          }}
          className={`inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium hover:bg-purple-100 transition-default truncate max-w-full font-body ${
            onFilterByEvent ? 'cursor-pointer' : 'cursor-default'
          }`}
          title={`Event: ${eventValue}`}
        >
          <Package className="icon-xs flex-shrink-0" />
          <span className="truncate">{eventValue}</span>
        </button>
      )}
      {hasReleaseDate && (() => {
        const { text, effectiveSeason } = getReleaseDateDisplay(item);
        return (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              effectiveSeason === 'Spring' ? 'bg-pink-50 text-pink-700' :
              effectiveSeason === 'Summer' ? 'bg-yellow-50 text-yellow-700' :
              effectiveSeason === 'Fall' ? 'bg-orange-50 text-orange-700' :
              effectiveSeason === 'Winter' ? 'bg-blue-50 text-blue-700' :
              'bg-gray-50 text-gray-700'
            }`}
            title={text}
          >
            {effectiveSeason === 'Spring' && '🌷'}
            {effectiveSeason === 'Summer' && '☀️'}
            {effectiveSeason === 'Fall' && '🍂'}
            {effectiveSeason === 'Winter' && '❄️'}
            {!effectiveSeason && '📅'}
            {` ${text}`}
          </span>
        );
      })()}
    </div>
  );
}
